import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart, ShoppingCart } from 'lucide-react';
import { useWishlist } from '../context/WishlistContext';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const getOptimizedUrl = (url, width = 400) => {
  if (!url) return '';
  if (url.startsWith('/api/uploads/')) {
    const filename = url.replace('/api/uploads/', '');
    return `${BACKEND}/api/img/${filename}?w=${width}&q=70`;
  }
  return url;
};

// Cache fetched pricing so we only call the API once
let cachedSizes = null;
let fetchingPromise = null;

const fetchGlobalSizes = () => {
  if (cachedSizes) return Promise.resolve(cachedSizes);
  if (fetchingPromise) return fetchingPromise;
  fetchingPromise = axios.get(`${API}/pricing`)
    .then(res => { cachedSizes = res.data.sizes; return cachedSizes; })
    .catch(() => {
      cachedSizes = [
        { size: '4x6', price: 11.99 },
        { size: '13x19', price: 22.99 },
        { size: '24x36', price: 32.99 },
        { size: '24x40', price: 49.99 },
        { size: '36x48', price: 134.99 },
      ];
      return cachedSizes;
    })
    .finally(() => { fetchingPromise = null; });
  return fetchingPromise;
};

const ProductCard = ({ product, index = 0 }) => {
  const { isInWishlist, addToWishlist, removeFromWishlist } = useWishlist();
  const { addToCart } = useCart();
  const inWishlist = isInWishlist(product.id);
  const [showSizes, setShowSizes] = useState(false);
  const [globalSizes, setGlobalSizes] = useState(null);

  useEffect(() => {
    if (!product.sizes?.length) {
      fetchGlobalSizes().then(setGlobalSizes);
    }
  }, [product.sizes]);

  const sizes = product.sizes?.length ? product.sizes : (globalSizes || []);
  const productUrl = product.slug ? `/products/${product.slug}` : `/product/${product.id}`;
  const imgAlt = `${product.title} ${product.category || ''} poster`.trim();

  const handleWishlistToggle = async () => {
    try {
      if (inWishlist) { await removeFromWishlist(product.id); toast.success('Removed from wishlist'); }
      else { await addToWishlist(product.id); toast.success('Added to wishlist'); }
    } catch { toast.error('Failed to update wishlist'); }
  };

  const handleAddWithSize = async (size) => {
    try {
      await addToCart(product.id, 1, size);
      toast.success(`${size} added to cart!`);
      setShowSizes(false);
    } catch { toast.error('Failed to add to cart'); }
  };

  return (
    <div
      className="product-card group block bg-black/40 backdrop-blur-sm rounded overflow-hidden hover:bg-black/60 transition-all"
      data-testid={`product-card-${product.id}`}
      onMouseLeave={() => setShowSizes(false)}
    >
      <div className="relative overflow-hidden poster-image-wrap">
        <Link to={productUrl}>
          <img
            src={getOptimizedUrl(product.image_url, 400)}
            alt={imgAlt}
            className="w-full aspect-[3/4] object-cover poster-image"
            loading="lazy"
            decoding="async"
            draggable="false"
            onContextMenu={(e) => e.preventDefault()}
          />
        </Link>
        <button
          onClick={handleWishlistToggle}
          className={`absolute top-2 right-2 p-1.5 transition-all z-10 ${
            inWishlist ? 'bg-fire text-white' : 'bg-black/70 text-white hover:bg-fire'
          }`}
          data-testid={`wishlist-toggle-${product.id}`}
        >
          <Heart className={`w-3 h-3 ${inWishlist ? 'fill-current' : ''}`} />
        </button>
        {product.featured && (
          <span className="absolute top-2 left-2 bg-fire text-white text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5">
            Featured
          </span>
        )}

        {/* Size picker overlay */}
        {showSizes ? (
          <div className="absolute bottom-0 left-0 right-0 bg-gray-950/95 backdrop-blur-sm p-2 z-10 animate-in slide-in-from-bottom-2" data-testid={`size-picker-${product.id}`}>
            <p className="text-[10px] text-gray-400 uppercase tracking-wider text-center mb-1.5">Select Size</p>
            <div className="flex gap-1 justify-center">
              {sizes.map((s) => (
                <button
                  key={s.size}
                  onClick={() => handleAddWithSize(s.size)}
                  className="flex-1 py-1.5 bg-gray-800 hover:bg-gradient-to-r hover:from-red-500 hover:to-orange-500 text-white text-[10px] font-bold rounded transition-all text-center"
                  data-testid={`size-${product.id}-${s.size}`}
                >
                  <span className="block">{s.size}</span>
                  <span className="block text-[9px] text-gray-400 group-hover:text-white/70">${s.price.toFixed(2)}</span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowSizes(true)}
            className="absolute bottom-0 left-0 right-0 py-2 bg-gradient-to-r from-red-500 to-orange-500 text-white text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-1.5 translate-y-full group-hover:translate-y-0 transition-transform duration-200 z-10"
            data-testid={`add-cart-${product.id}`}
          >
            <ShoppingCart className="w-3 h-3" />
            Add to Cart
          </button>
        )}
      </div>
      <Link to={productUrl} className="block p-2">
        <span className="text-[10px] uppercase tracking-wider text-white/50">{product.category}</span>
        <h3 className="font-heading text-xs text-white mt-0.5 line-clamp-2 group-hover:text-fire transition-colors leading-tight">
          {product.title}
        </h3>
        <div className="mt-1 flex items-center gap-1 flex-wrap">
          <p className="font-heading text-sm text-fire font-bold">From ${product.price.toFixed(2)}</p>
          {product.original_price && product.original_price > product.price && (
            <>
              <p className="text-[10px] text-white/50 line-through">${product.original_price.toFixed(2)}</p>
              <span className="text-[8px] bg-fire text-white px-1 py-0.5 font-bold">
                {product.discount?.type === 'percentage' ? `${product.discount.value}%` : `$${product.discount?.value}`}
              </span>
            </>
          )}
        </div>
      </Link>
    </div>
  );
};

export default ProductCard;
